// src/pages/VoterLookup.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Globe, Phone, CheckCircle, AlertTriangle, ClipboardList, Landmark, Lock,
  ArrowUpRight, Mail, Vote, MapPin, Star, Info, ArrowRight, User, Home, Search, Map, Loader,
} from 'lucide-react';
import './VoterLookup.css';
import { EXTERNAL_LINKS } from '../utils/theme';
import { lookupVoter, fetchPollingLocations, fetchOfficialsByDistrict } from '../utils/airtable';
import { fetchWeldVotingLocations, nearestLocations, isDropOffType } from '../utils/weldLocations';
import { lookupWeldSchoolDistrictCode, mapWeldSchoolDistrictToAirtable } from '../utils/weldSchoolDistricts';
import { lookupWeldPrecinct } from '../utils/weldPrecincts';

const LOOKUP_MODES = { PERSON: 'person', ADDRESS: 'address' };

const VOTERGO_STORAGE_KEYS = {
  firstName: 'votergo_firstName',
  lastName: 'votergo_lastName',
  zipCode: 'votergo_zipCode',
  birthYear: 'votergo_birthYear',
  address: 'votergo_address',
};

const LARIMER_GIS = 'https://maps1.larimer.org/arcgis/rest/services/MapServices/voterMap/MapServer';
const TIGERWEB = 'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb';

const ZIP_TO_CITY = {
  '80521': 'Fort Collins', '80522': 'Fort Collins', '80523': 'Fort Collins',
  '80524': 'Fort Collins', '80525': 'Fort Collins', '80526': 'Fort Collins',
  '80527': 'Fort Collins', '80528': 'Fort Collins', '80513': 'Berthoud',
  '80515': 'Drake', '80516': 'Windsor', '80517': 'Estes Park',
  '80535': 'Laporte', '80536': 'Livermore', '80537': 'Loveland',
  '80538': 'Loveland', '80539': 'Loveland', '80540': 'Lyons',
  '80545': 'Red Feather Lakes', '80546': 'Severance', '80547': 'Timnath',
  '80549': 'Wellington', '80550': 'Windsor', '80551': 'Windsor', '80553': 'Fort Collins',
};

// Map city names to Google My Maps IDs from Larimer County Clerk
const getCityMapId = (city) => {
  const normalized = (city || '').split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
  const cityMaps = {
    'Fort Collins': '1hXVcUbkTV4Gw3p60qASSWe8RhiUsn58',
    'Loveland': '19b3QtvyczZM9fshGqr6na_fULqUP9JQ',
    'Berthoud': '16eS00rwlvGTGQCMmUWRbEPAGAMMPoRg',
    'Estes Park': '1s1jZbiytittdxUBTDRtWRFw-7-qwijQ',
    'Wellington': '1HzEERVSyXh2CCz_2clkX1tFMRGhiwDs',
    'Timnath': '1RoXXiIcm2uSMoQ9AUuVszREfp8UKrEA',
    'Red Feather Lakes': '16SOtAR5eaIc4If6XkRJVR4mK5HNf0CU',
    'Laporte': '1NXTFTGiWlYg5AUdEXRgbG3KSmNh9WMQ',
    'Windsor': '1hXVcUbkTV4Gw3p60qASSWe8RhiUsn58',
    'Severance': '1hXVcUbkTV4Gw3p60qASSWe8RhiUsn58',
  };
  return cityMaps[normalized] || '1SIy5Qs82jl-01-aha3btNK_Umtjb7hk';
};

const fetchDistricts = async (lat, lng, isLarimer) => {
  const districts = {};
  const larimerQueries = isLarimer
    ? [
        fetch(`${LARIMER_GIS}/identify?geometry=${lng},${lat}&geometryType=esriGeometryPoint&sr=4326&layers=all:6&tolerance=2&mapExtent=${lng - 0.1},${lat - 0.1},${lng + 0.1},${lat + 0.1}&imageDisplay=800,600,96&returnGeometry=false&f=json`).then(r => r.json()),
        fetch(`${LARIMER_GIS}/identify?geometry=${lng},${lat}&geometryType=esriGeometryPoint&sr=4326&layers=all:2&tolerance=2&mapExtent=${lng - 0.1},${lat - 0.1},${lng + 0.1},${lat + 0.1}&imageDisplay=800,600,96&returnGeometry=false&f=json`).then(r => r.json()),
        // Same TIGERweb School service the district map uses for Larimer's School Board layer
        fetch(`${TIGERWEB}/School/MapServer/0/query?geometry=${lng},${lat}&geometryType=esriGeometryPoint&inSR=4326&spatialRel=esriSpatialRelWithin&outFields=*&f=geojson`).then(r => r.json()),
      ]
    : [Promise.resolve(null), Promise.resolve(null), Promise.resolve(null)];

  const results = await Promise.allSettled([
    fetch(`${TIGERWEB}/Legislative/MapServer/0/query?geometry=${lng},${lat}&geometryType=esriGeometryPoint&inSR=4326&spatialRel=esriSpatialRelWithin&outFields=*&f=geojson`).then(r => r.json()),
    fetch(`${TIGERWEB}/Legislative/MapServer/1/query?geometry=${lng},${lat}&geometryType=esriGeometryPoint&inSR=4326&spatialRel=esriSpatialRelWithin&outFields=*&f=geojson`).then(r => r.json()),
    fetch(`${TIGERWEB}/Legislative/MapServer/2/query?geometry=${lng},${lat}&geometryType=esriGeometryPoint&inSR=4326&spatialRel=esriSpatialRelWithin&outFields=*&f=geojson`).then(r => r.json()),
    ...larimerQueries,
  ]);

  const [congressional, stateSenate, stateHouse, county, precinct, school] = results;

  if (congressional.status === 'fulfilled' && congressional.value?.features?.[0]) {
    const props = congressional.value.features[0].properties;
    const raw = props.NAMELSAD || props.NAME || '';
    const match = raw.match(/(\d+)/);
    districts.congressional = match ? `CO-${match[1]}` : raw;
  }
  if (stateSenate.status === 'fulfilled' && stateSenate.value?.features?.[0]) {
    const props = stateSenate.value.features[0].properties;
    const raw = props.NAMELSAD || props.NAME || '';
    const match = raw.match(/(\d+)/);
    districts.stateSenate = match ? `SD-${match[1]}` : raw;
  }
  if (stateHouse.status === 'fulfilled' && stateHouse.value?.features?.[0]) {
    const props = stateHouse.value.features[0].properties;
    const raw = props.NAMELSAD || props.NAME || '';
    const match = raw.match(/(\d+)/);
    districts.stateHouse = match ? `HD-${match[1]}` : raw;
  }
  if (county.status === 'fulfilled' && county.value?.results?.[0]) {
    const attrs = county.value.results[0].attributes || {};
    const raw = attrs.DISTRICT || attrs.District || attrs.NAME || attrs.Name || '';
    districts.county = raw ? `District ${raw}` : '';
  }
  if (precinct.status === 'fulfilled' && precinct.value?.results?.[0]) {
    const attrs = precinct.value.results[0].attributes || {};
    districts.precinct = attrs.PRECINCT || attrs.Precinct || attrs.NAME || attrs.Name || String(precinct.value.results[0].value || '');
  }
  if (school.status === 'fulfilled' && school.value?.features?.[0]) {
    const props = school.value.features[0].properties;
    districts.school = props.NAMELSAD || props.NAME || props.GEOID || '';
  }

  return districts;
};

const getOfficeRank = (office) => {
  const o = (office || '').toLowerCase().trim();
  if (o.includes('vice president'))       return 1;
  if (o.includes('president'))            return 0;
  if (o.includes('u.s. senator') || o.includes('us senator') || o.includes('united states senator')) return 2;
  if (o.includes('congressman') || o.includes('u.s. representative') || o.includes('us representative')) return 3;
  if (o.includes('lieutenant governor'))  return 5;
  if (o.includes('governor'))             return 4;
  if (o.includes('secretary of state'))   return 6;
  if (o.includes('attorney general'))     return 7;
  if (o.includes('treasurer'))            return 8;
  if (o.includes('state senator'))        return 9;
  if (o.includes('state representative')) return 10;
  if (o.includes('county commissioner'))  return 11;
  return 12;
};

const decodePrecinctDistricts = (precinct) => {
  if (!precinct) return {};
  const p = String(precinct).padStart(10, '0');
  const congressional = parseInt(p[0], 10);
  const stateSenate = parseInt(p.slice(1, 3), 10);
  const stateHouse = parseInt(p.slice(3, 5), 10);
  return {
    congressional: congressional ? `CO-${congressional}` : '',
    stateSenate: stateSenate ? `SD-${stateSenate}` : '',
    stateHouse: stateHouse ? `HD-${stateHouse}` : '',
  };
};

const OfficialCard = ({ official }) => {
  const initials = official.Name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '??';
  const photoUrl = official.Photo?.[0]?.url;
  const bgColor = official.Party === 'Republican' ? '#ae3030'
    : official.Party === 'Democrat' ? '#182B45'
    : '#5f5e5a';

  return (
    <div className="official-card">
      <div className="official-avatar" style={{ background: bgColor }}>
        {photoUrl
          ? <img src={photoUrl} alt={official.Name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
          : initials
        }
      </div>
      <div>
        <div className="official-role">{official.Office}</div>
        <div className="official-name">{official.Name}</div>
        <div className="official-party">{official.Party} · {official.District}</div>
        {official.Bio && (
          <div style={{ fontSize: 11, color: '#666', lineHeight: 1.5, margin: '4px 0 6px' }}>
            {official.Bio}
          </div>
        )}
        <div className="official-links">
          {official.Website && <a href={official.Website} className="official-link" target="_blank" rel="noreferrer"><Globe size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Website</a>}
          {official.Phone && <a href={`tel:${official.Phone}`} className="official-link"><Phone size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> {official.Phone}</a>}
        </div>
      </div>
    </div>
  );
};

const WELD_RESULTS_MARKER_ICONS = {
  vspc: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
  dropoff: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
};

const buildWeldResultsInfoWindow = (loc) => {
  const wrap = document.createElement('div');
  wrap.style.fontSize = '13px';
  wrap.style.lineHeight = '1.5';
  wrap.style.maxWidth = '220px';

  const name = document.createElement('div');
  name.style.fontWeight = '600';
  name.style.marginBottom = '2px';
  name.textContent = loc.name;

  const addr = document.createElement('div');
  addr.style.color = '#444';
  addr.textContent = loc.address;

  const type = document.createElement('div');
  type.style.fontWeight = '500';
  type.style.marginTop = '4px';
  type.style.color = isDropOffType(loc.type) ? '#1a56db' : '#ae3030';
  type.textContent = loc.type;

  wrap.appendChild(name);
  wrap.appendChild(addr);
  wrap.appendChild(type);
  return wrap;
};

// Compact voting-locations map embedded at the bottom of a Weld County
// lookup result — all 27 locations, colored by type, click for details.
const WeldResultsMap = ({ mapsLoaded }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const mapInitialized = useRef(false);
  const infoWindowRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    fetchWeldVotingLocations()
      .then(data => { if (!cancelled) setLocations(data); })
      .catch(err => {
        console.log('Weld voting locations fetch error:', err);
        if (!cancelled) setError('Could not load voting locations.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // The map is created exactly once, directly inside a div React never
  // renders children into — Google Maps owns that node's DOM from here on.
  useEffect(() => {
    if (!mapsLoaded || !window.google || loading || !mapRef.current || locations.length === 0 || mapInitialized.current) return;

    googleMapRef.current = new window.google.maps.Map(mapRef.current, {
      zoom: 9,
      center: { lat: 40.42, lng: -104.7 },
      mapTypeId: 'roadmap',
    });
    infoWindowRef.current = new window.google.maps.InfoWindow();
    mapInitialized.current = true;

    const bounds = new window.google.maps.LatLngBounds();
    locations.forEach(loc => {
      const dropoff = isDropOffType(loc.type);
      const marker = new window.google.maps.Marker({
        position: { lat: loc.lat, lng: loc.lng },
        map: googleMapRef.current,
        title: loc.name,
        icon: dropoff ? WELD_RESULTS_MARKER_ICONS.dropoff : WELD_RESULTS_MARKER_ICONS.vspc,
      });
      marker.addListener('click', () => {
        infoWindowRef.current.setContent(buildWeldResultsInfoWindow(loc));
        infoWindowRef.current.open(googleMapRef.current, marker);
      });
      markersRef.current.push(marker);
      bounds.extend({ lat: loc.lat, lng: loc.lng });
    });
    googleMapRef.current.fitBounds(bounds);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsLoaded, loading, locations]);

  // Teardown on unmount: clear markers/listeners ourselves so React never
  // has to reconcile DOM nodes Google Maps already rewrote.
  useEffect(() => {
    return () => {
      markersRef.current.forEach(marker => {
        if (window.google) window.google.maps.event.clearInstanceListeners(marker);
        marker.setMap(null);
      });
      markersRef.current = [];
      if (infoWindowRef.current) infoWindowRef.current.close();
      googleMapRef.current = null;
      mapInitialized.current = false;
    };
  }, []);

  return (
    <div>
      {error && <div className="error-msg" style={{ marginBottom: 8 }}>{error}</div>}
      <div
        style={{
          position: 'relative',
          height: 300,
          borderRadius: 6,
          border: '0.5px solid #ddd',
          marginBottom: 10,
          background: '#f0f4f8',
          overflow: 'hidden',
        }}
      >
        {/* Google Maps owns this node's DOM once created — React never renders children into it. */}
        <div ref={mapRef} style={{ position: 'absolute', inset: 0 }} />

        {(loading || !mapsLoaded) && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', color: '#999', pointerEvents: 'none',
          }}>
            <div style={{ fontSize: 13 }}><Loader size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Loading voting locations…</div>
          </div>
        )}
      </div>
    </div>
  );
};

const LookupResults = ({ onReset, civicData, civicError, setActiveTab, county, mapsLoaded }) => {
  const officials = [...(civicData?.officials || [])].sort((a, b) => getOfficeRank(a.Office) - getOfficeRank(b.Office));

  return (
    <div className="results-area">
      <div className="results-header">
        <span><CheckCircle size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Voter information found</span>
        <button className="btn-outline" onClick={onReset}>New lookup</button>
      </div>

      {civicError && <div className="error-msg"><AlertTriangle size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> {civicError}</div>}

      <div className="result-section">
        <div className="result-section-title"><ClipboardList size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Your registration</div>
        <div className="result-grid">
          {civicData?.firstName && (
            <div className="result-card">
              <div className="result-label">Name</div>
              <div className="result-value">{civicData.firstName} {civicData.lastName}</div>
            </div>
          )}
          <div className="result-card">
            <div className="result-label">City</div>
            <div className="result-value">{civicData?.city || county.name}</div>
          </div>
          {civicData?.zipCode && (
            <div className="result-card">
              <div className="result-label">Zip code</div>
              <div className="result-value">{civicData.zipCode}</div>
            </div>
          )}
          <div className="result-card">
            <div className="result-label">County</div>
            <div className="result-value">{county.name}</div>
          </div>
          {civicData?.status && (
            <div className="result-card">
              <div className="result-label">Registration status</div>
              <div className="result-value">{civicData.status}</div>
            </div>
          )}
          {civicData?.registrationDate && (
            <div className="result-card">
              <div className="result-label">Date registered</div>
              <div className="result-value">{new Date(civicData.registrationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>
          )}
          {civicData?.districts?.congressional && (
            <div className="result-card">
              <div className="result-label">Congressional District</div>
              <div className="result-value">{civicData.districts.congressional}</div>
            </div>
          )}
          {civicData?.districts?.stateSenate && (
            <div className="result-card">
              <div className="result-label">State Senate District</div>
              <div className="result-value">{civicData.districts.stateSenate}</div>
            </div>
          )}
          {civicData?.districts?.stateHouse && (
            <div className="result-card">
              <div className="result-label">State House District</div>
              <div className="result-value">{civicData.districts.stateHouse}</div>
            </div>
          )}
          {civicData?.districts?.school ? (
            <div className="result-card">
              <div className="result-label">School Board District</div>
              <div className="result-value">{civicData.districts.school}</div>
            </div>
          ) : (
            county.id !== 'weld' && civicData?.districts?.stateHouse && (
              <div className="result-card" style={{ visibility: 'hidden' }} aria-hidden="true" />
            )
          )}
          {civicData?.precinct && county.id !== 'weld' && (
            <div className="result-card">
              <div className="result-label"><ClipboardList size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Precinct</div>
              <div className="result-value">{civicData.precinct.slice(-3)}</div>
            </div>
          )}
          {county.id === 'weld' && civicData?.districts?.precinctNumber && (
            <div className="result-card">
              <div className="result-label"><ClipboardList size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Precinct</div>
              <div className="result-value">{civicData.districts.precinctNumber}</div>
            </div>
          )}
          {civicData?.districts?.county && (
            <div className="result-card">
              <div className="result-label"><Landmark size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> County Commissioner District</div>
              <div className="result-value">{civicData.districts.county}</div>
            </div>
          )}
          {county.id === 'weld' && civicData?.districts?.commissioner && (
            <div className="result-card">
              <div className="result-label"><Landmark size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Commissioner District</div>
              <div className="result-value">{civicData.districts.commissioner}</div>
            </div>
          )}
          <div className="result-card">
            <div className="result-label">Next election</div>
            <div className="result-value"><span className="badge badge-amber">November 3, 2026 — General</span></div>
          </div>
        </div>
        <div style={{ background: '#f0f4f8', borderRadius: 6, padding: '12px 14px', marginTop: 10, fontSize: 13, color: '#0c447c', lineHeight: 1.6 }}>
          <Lock size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> For your complete voter record including voter ID number, party affiliation, and previous voting history — view your official record securely at the Colorado Secretary of State.
        </div>
        <div className="ext-links">
          <a className="ext-btn red" href="https://www.sos.state.co.us/voter/pages/pub/olvr/findVoterReg.xhtml" target="_blank" rel="noreferrer"><ArrowUpRight size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> View full record at CO SOS</a>
        </div>
      </div>

      <div className="result-section">
        <div className="result-section-title"><Mail size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Your ballot — 2026 General</div>
        <div style={{ background: '#f0f4f8', borderRadius: 6, border: '0.5px solid #c8d8e8', padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 24 }}><Mail size={20} color="currentColor" style={{ verticalAlign: 'middle' }} /></span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#0a2240', marginBottom: 4 }}>Track your 2026 General ballot</div>
            <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6, marginBottom: 10 }}>
              Check the real-time status of your mail ballot directly at the Colorado Secretary of State.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <a className="ext-btn red" href={EXTERNAL_LINKS.ballotTrax} target="_blank" rel="noreferrer"><Mail size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Track my ballot</a>
              <a className="ext-btn" href={EXTERNAL_LINKS.sampleBallot} target="_blank" rel="noreferrer"><Vote size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> View Sample Ballot</a>
              <a className="ext-btn" href={county.id === 'larimer' ? EXTERNAL_LINKS.larimerClerk : county.whereToVoteUrl} target="_blank" rel="noreferrer"><MapPin size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> {county.name} Clerk <ArrowRight size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /></a>
            </div>
          </div>
        </div>
      </div>

      {officials.length > 0 && (
  <div className="result-section">
    <div className="result-section-title"><Star size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Your elected officials</div>
    {civicData?.districts && Object.keys(civicData.districts).length > 0 && (
      <div style={{ fontSize: 12, color: '#666', marginBottom: 10, lineHeight: 1.6 }}>
        Based on your districts: {Object.values(civicData.districts).filter(Boolean).join(' · ')}
      </div>
    )}
    <div className="official-grid">
      {officials.map((official, i) => (
        <OfficialCard key={i} official={official} />
      ))}
    </div>
    {(!civicData?.districts || Object.keys(civicData.districts).length === 0) && (
      <div style={{ marginTop: 10, padding: '10px 14px', background: '#f0f4f8', borderRadius: 6, fontSize: 12, color: '#0c447c', lineHeight: 1.6 }}>
        <Info size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Showing statewide and federal officials only. To see your locally elected officials (Congressional, State House, State Senate, County Commissioner, etc.) click <strong>New lookup</strong> above and use <strong>Look up by address</strong> with your full street address.
      </div>
    )}
  </div>
)}

      <div className="result-section">
  <div className="result-section-title"><MapPin size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Where to vote</div>
  {county.id === 'weld' ? (
    <>
      <WeldResultsMap mapsLoaded={mapsLoaded} />
      <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
        The 3 nearest voting locations to {civicData?.city || 'your address'}.
      </div>
      {civicData?.nearestVotingLocations?.length > 0 ? (
        civicData.nearestVotingLocations.map((loc, i) => (
          <div className="location-card" key={i}>
            <span className="location-icon">{loc.type === 'VSPC' ? <Vote size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> : <Mail size={16} color="currentColor" style={{ verticalAlign: 'middle' }} />}</span>
            <div>
              <div className="location-name">{loc.name}</div>
              <div className="location-addr">{loc.address}</div>
              <div className="location-hours">{loc.type}</div>
              <div className="location-dist">{loc.distance.toFixed(1)} mi away</div>
            </div>
          </div>
        ))
      ) : (
        <div style={{ fontSize: 12, color: '#999', marginBottom: 10 }}>
          Nearby voting locations unavailable — view the full list below.
        </div>
      )}
    </>
  ) : (
    <>
      <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
        Ballot boxes (yellow) and vote centers (red) near {civicData?.city || 'your location'} for the 2026 General Election.
      </div>
      <iframe
        src={county.whereToVoteType === 'iframe'
          ? county.whereToVoteUrl
          : `https://www.google.com/maps/d/u/0/embed?mid=${getCityMapId(civicData?.city)}&ehbc=2E312F&noprof=1`}
        width="100%"
        height="300"
        frameBorder="0"
        title="Ballot boxes and vote centers"
        style={{ borderRadius: 6, border: '0.5px solid #ddd', marginBottom: 10 }}
        allowFullScreen
      />
    </>
  )}
  <div className="ext-links">
    <button className="ext-btn red" onClick={() => setActiveTab('wheretovote')}>
      <MapPin size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> View all locations
    </button>
    {county.id === 'weld' && (
      <a className="ext-btn" href="https://www.weld.gov/Government/Departments/Clerk-and-Recorder/Elections-Department" target="_blank" rel="noreferrer">
        <Landmark size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Weld County Clerk elections page
      </a>
    )}
  </div>
</div>
    </div>
  );
};

const VoterLookup = ({ setActiveTab, setSharedAddress, county, mapsLoaded }) => {
  const allowNameLookup = ['larimer', 'weld'].includes(county?.id);
  const [mode, setMode] = useState(allowNameLookup ? LOOKUP_MODES.PERSON : LOOKUP_MODES.ADDRESS);
  const [address, setAddress] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [hasResults, setHasResults] = useState(false);
  const [civicData, setCivicData] = useState(null);
  const [civicLoading, setCivicLoading] = useState(false);
  const [civicError, setCivicError] = useState(null);
  const [lookupError, setLookupError] = useState(null);
  const [personForm, setPersonForm] = useState({ firstName: '', lastName: '', zipCode: '', birthYear: '' });
  const [personAutofilled, setPersonAutofilled] = useState(false);
  const [addressAutofilled, setAddressAutofilled] = useState(false);
  const autocompleteService = useRef(null);
  const hideTimeout = useRef(null);

  useEffect(() => {
    const savedFirstName = localStorage.getItem(VOTERGO_STORAGE_KEYS.firstName);
    const savedLastName = localStorage.getItem(VOTERGO_STORAGE_KEYS.lastName);
    const savedZipCode = localStorage.getItem(VOTERGO_STORAGE_KEYS.zipCode);
    const savedBirthYear = localStorage.getItem(VOTERGO_STORAGE_KEYS.birthYear);
    if (savedFirstName || savedLastName || savedZipCode || savedBirthYear) {
      setPersonForm({
        firstName: savedFirstName || '',
        lastName: savedLastName || '',
        zipCode: savedZipCode || '',
        birthYear: savedBirthYear || '',
      });
      setPersonAutofilled(true);
    }

    const savedAddress = localStorage.getItem(VOTERGO_STORAGE_KEYS.address);
    if (savedAddress) {
      setAddress(savedAddress);
      setAddressAutofilled(true);
    }
  }, []);

  const clearSavedInfo = () => {
    Object.values(VOTERGO_STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
    setPersonForm({ firstName: '', lastName: '', zipCode: '', birthYear: '' });
    setAddress('');
    setPersonAutofilled(false);
    setAddressAutofilled(false);
  };

  const handleAddressInput = (val) => {
    setAddress(val);
    if (val.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (autocompleteService.current) {
      autocompleteService.current.getPlacePredictions(
        {
          input: val,
          componentRestrictions: { country: 'us' },
          bounds: county?.autocompleteBounds,
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions.map(p => ({
              main: p.structured_formatting.main_text,
              sub: p.structured_formatting.secondary_text,
            })));
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        }
      );
    }
  };

  const selectAddress = (addr) => {
    const full = `${addr.main}, ${addr.sub}`.replace(', USA', '').replace(', United States', '');
    setAddress(full);
    setShowSuggestions(false);
    handleAddressLookup(full);
  };

const geocodeAddress = (addr) => {
  return new Promise((resolve, reject) => {
    if (!window.google) {
      reject(new Error('Google Maps not loaded'));
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: addr }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        // Extract city from address components
        const components = results[0].address_components;
        const cityComponent = components.find(c =>
          c.types.includes('locality') || c.types.includes('sublocality')
        );
        const city = cityComponent?.long_name || '';
        resolve({
          lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
          lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
          city,
        });
      } else {
        reject(new Error(`Geocode failed: ${status}`));
      }
    });
  });
};
  const handleAddressLookup = async (lookupAddress) => {
    const addr = (lookupAddress || address).replace(', USA', '').replace(', United States', '');
    if (!addr.trim()) return;
    setCivicLoading(true);
    setCivicError(null);
    setShowSuggestions(false);

    try {
      const { lat, lng, city } = await geocodeAddress(addr);
      const districts = await fetchDistricts(lat, lng, county?.id === 'larimer');

      // A single Precinct GeoJSON point-in-polygon lookup carries precinct
      // number, commissioner district, and Congressional/Senate/House
      // numbers all at once — used for display and to match officials.
      let weldOfficialDistrictValues = [];
      if (county?.id === 'weld') {
        try {
          const schoolDistrictCode = await lookupWeldSchoolDistrictCode(lat, lng);
          if (schoolDistrictCode) {
            districts.school = mapWeldSchoolDistrictToAirtable(schoolDistrictCode) || schoolDistrictCode;
          }
        } catch (err) {
          console.log('Weld school district lookup failed:', err);
        }

        try {
          const precinctProps = await lookupWeldPrecinct(lat, lng);
          if (precinctProps) {
            if (precinctProps.PRECCODE != null) districts.precinctNumber = String(precinctProps.PRECCODE);
            if (precinctProps.COMMISS != null) districts.commissioner = `District ${precinctProps.COMMISS}`;
            if (precinctProps.CONGRESS != null) weldOfficialDistrictValues.push(`CD-${precinctProps.CONGRESS}`);
            if (precinctProps.SENATE != null) weldOfficialDistrictValues.push(`SD-${precinctProps.SENATE}`);
            if (precinctProps.HOUSE != null) weldOfficialDistrictValues.push(`HD-${precinctProps.HOUSE}`);
          }
        } catch (err) {
          console.log('Weld precinct lookup failed:', err);
        }
      }

      const districtValues = [
        districts.congressional,
        districts.stateSenate,
        districts.stateHouse,
        districts.county,
        districts.school,
        districts.commissioner,
        ...weldOfficialDistrictValues,
      ].filter(Boolean);

      let officials = [];
      try {
        officials = await fetchOfficialsByDistrict(districtValues);
      } catch (err) {
        console.log('Could not fetch officials from Airtable:', err);
      }

      let pollingLocations = [];
      let dropBoxes = [];
      let earlyVoteSites = [];
      try {
        const voterInfo = await fetchPollingLocations(addr);
        pollingLocations = voterInfo.pollingLocations || [];
        dropBoxes = voterInfo.dropOffLocations || [];
        earlyVoteSites = voterInfo.earlyVoteSites || [];
      } catch (err) {
        console.log('Polling location data unavailable');
      }

      let nearestVotingLocations = [];
      if (county?.id === 'weld') {
        try {
          const weldLocations = await fetchWeldVotingLocations();
          nearestVotingLocations = nearestLocations(weldLocations, lat, lng, 3);
        } catch (err) {
          console.log('Weld voting locations unavailable:', err);
        }
      }

      setCivicData({ officials, districts, city, precinct: districts.precinct || '', pollingLocations, dropBoxes, earlyVoteSites, lat, lng, nearestVotingLocations });
      if (setSharedAddress) setSharedAddress(addr);
      setHasResults(true);
      localStorage.setItem(VOTERGO_STORAGE_KEYS.address, addr);
      setAddressAutofilled(false);
    } catch (err) {
      console.log('Address lookup error:', err);
      setCivicError('Could not fetch voter information. Please try again.');
      setHasResults(true);
    } finally {
      setCivicLoading(false);
    }
  };

  const handlePersonLookup = async () => {
    if (!personForm.firstName || !personForm.lastName || !personForm.zipCode || !personForm.birthYear) {
      setLookupError('Please fill in your first name, last name, zip code, and birth year.');
      return;
    }
    setCivicLoading(true);
    setLookupError(null);
    try {
      const result = await lookupVoter({ ...personForm, county: county?.id });
      const city = result.voterInfo?.city || ZIP_TO_CITY[personForm.zipCode] || county?.name || '';
      const precinct = result.voterInfo?.precinct || '';
      const districts = decodePrecinctDistricts(precinct);

      let approxLatLng = null;
      if (county?.id === 'weld') {
        // Precinct number comes straight from Supabase; commissioner district
        // isn't a Supabase column, so it's derived the same way as school
        // district — via the geocoded city/zip centroid.
        districts.precinctNumber = precinct;

        try {
          approxLatLng = await geocodeAddress(`${city}, CO ${personForm.zipCode}`);
          const schoolDistrictCode = await lookupWeldSchoolDistrictCode(approxLatLng.lat, approxLatLng.lng);
          if (schoolDistrictCode) {
            districts.school = mapWeldSchoolDistrictToAirtable(schoolDistrictCode) || schoolDistrictCode;
          }
        } catch (err) {
          console.log('Weld school district lookup failed:', err);
        }

        if (approxLatLng) {
          try {
            const precinctProps = await lookupWeldPrecinct(approxLatLng.lat, approxLatLng.lng);
            if (precinctProps?.COMMISS != null) {
              districts.commissioner = `District ${precinctProps.COMMISS}`;
            }
          } catch (err) {
            console.log('Weld commissioner district lookup failed:', err);
          }
        }
      } else if (county?.id === 'larimer') {
        // Same TIGERweb School service the district map uses for Larimer's School Board layer
        try {
          approxLatLng = await geocodeAddress(`${city}, CO ${personForm.zipCode}`);
          const schoolResult = await fetch(`${TIGERWEB}/School/MapServer/0/query?geometry=${approxLatLng.lng},${approxLatLng.lat}&geometryType=esriGeometryPoint&inSR=4326&spatialRel=esriSpatialRelWithin&outFields=*&f=geojson`).then(r => r.json());
          const props = schoolResult?.features?.[0]?.properties;
          if (props) {
            districts.school = props.NAMELSAD || props.NAME || props.GEOID || '';
          }
        } catch (err) {
          console.log('Larimer school district lookup failed:', err);
        }
      }

      const districtValues = [
        districts.congressional,
        districts.stateSenate,
        districts.stateHouse,
        districts.school,
        districts.commissioner,
      ].filter(Boolean);

      let officials = [];
      try {
        officials = await fetchOfficialsByDistrict(districtValues.length > 0 ? districtValues : []);
      } catch (err) {
        console.log('Could not fetch officials');
      }

      let pollingLocations = [];
      let dropBoxes = [];
      let earlyVoteSites = [];
      try {
        const voterInfo = await fetchPollingLocations(`${city}, CO ${personForm.zipCode}`);
        pollingLocations = voterInfo.pollingLocations || [];
        dropBoxes = voterInfo.dropOffLocations || [];
        earlyVoteSites = voterInfo.earlyVoteSites || [];
      } catch (err) {
        console.log('Polling location data unavailable');
      }

      let nearestVotingLocations = [];
      if (county?.id === 'weld') {
        try {
          const { lat, lng } = approxLatLng || await geocodeAddress(`${city}, CO ${personForm.zipCode}`);
          const weldLocations = await fetchWeldVotingLocations();
          nearestVotingLocations = nearestLocations(weldLocations, lat, lng, 3);
        } catch (err) {
          console.log('Weld voting locations unavailable:', err);
        }
      }

      setCivicData({
        ...result.voterInfo,
        city,
        precinct,
        officials,
        districts,
        pollingLocations,
        dropBoxes,
        earlyVoteSites,
        nearestVotingLocations,
      });
      setHasResults(true);
      localStorage.setItem(VOTERGO_STORAGE_KEYS.firstName, personForm.firstName);
      localStorage.setItem(VOTERGO_STORAGE_KEYS.lastName, personForm.lastName);
      localStorage.setItem(VOTERGO_STORAGE_KEYS.zipCode, personForm.zipCode);
      localStorage.setItem(VOTERGO_STORAGE_KEYS.birthYear, personForm.birthYear);
      setPersonAutofilled(false);
    } catch (err) {
      setLookupError('Could not complete lookup. Please try again.');
    } finally {
      setCivicLoading(false);
    }
  };

  const handleReset = () => {
    setHasResults(false);
    setAddress('');
    setCivicData(null);
    setCivicError(null);
    setLookupError(null);
    setPersonForm({ firstName: '', lastName: '', zipCode: '', birthYear: '' });
  };

  return (
    <div>
      <div className="sec-head">
        <div>
          <div className="sec-title">Voter Information Lookup</div>
          <div className="sec-desc">
            {allowNameLookup
              ? <>Use <strong>name lookup</strong> to see your personal voter record, registration details, and elected officials. Use <strong>address lookup</strong> to find districts, officials, and voting locations for any {county.name} address.</>
              : <>Use <strong>address lookup</strong> to find your districts, elected officials, and voting locations for any {county?.name} address.</>
            }
          </div>
        </div>
      </div>

      {!hasResults && !civicLoading && (
        <>
          {allowNameLookup && (
            <div className="lookup-tabs">
              <button
                className={`lookup-tab ${mode === LOOKUP_MODES.PERSON ? 'active' : ''}`}
                onClick={() => setMode(LOOKUP_MODES.PERSON)}
              >
                <User size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Look up by name
              </button>
              <button
                className={`lookup-tab ${mode === LOOKUP_MODES.ADDRESS ? 'active' : ''}`}
                onClick={() => setMode(LOOKUP_MODES.ADDRESS)}
              >
                <Home size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Look up by address
              </button>
            </div>
          )}

          {allowNameLookup && mode === LOOKUP_MODES.PERSON && (
            <div className="lookup-form">
              <div className="security-note">
                <Info size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Enter your name, zip code, and birth year to look up your personal voter record, registration status, party affiliation, districts, and elected officials.
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">First name</label>
                  <input type="text" placeholder="Jane" value={personForm.firstName} onChange={e => setPersonForm({ ...personForm, firstName: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Last name</label>
                  <input type="text" placeholder="Doe" value={personForm.lastName} onChange={e => setPersonForm({ ...personForm, lastName: e.target.value })} />
                </div>
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Zip code</label>
                  <input type="text" placeholder="80521" maxLength={5} value={personForm.zipCode} onChange={e => setPersonForm({ ...personForm, zipCode: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Birth year</label>
                  <input type="text" placeholder="1985" maxLength={4} value={personForm.birthYear} onChange={e => setPersonForm({ ...personForm, birthYear: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button className="btn-red" onClick={handlePersonLookup} disabled={civicLoading}>
                  <Search size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Look up my information
                </button>
                <button className="btn-outline" onClick={handleReset}>Clear</button>
              </div>
              {lookupError && <div className="error-msg" style={{ marginTop: 10 }}>{lookupError}</div>}
              {personAutofilled && (
                <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                  Auto-filled from your last lookup · <a href="#clear-saved-info" onClick={e => { e.preventDefault(); clearSavedInfo(); }} style={{ color: '#999', textDecoration: 'underline' }}>Clear</a>
                </div>
              )}
            </div>
          )}

          {mode === LOOKUP_MODES.ADDRESS && (
            <div className="lookup-form">
              <div className="security-note" style={{ background: '#e6eef5', borderColor: '#c8d8e8', color: '#0c447c' }}>
                <Info size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Enter any {county?.name} address to find its precinct, districts, elected officials, and nearby voting locations.
              </div>
              <div className="form-group" style={{ position: 'relative' }}>
                <label className="form-label">Street address</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    value={address}
                    placeholder="Start typing your address..."
                    onChange={e => handleAddressInput(e.target.value)}
                    onBlur={() => { hideTimeout.current = setTimeout(() => setShowSuggestions(false), 200); }}
                    onFocus={() => address.length > 1 && setShowSuggestions(suggestions.length > 0)}
                    autoComplete="off"
                  />
                  <button className="btn-red" onClick={() => handleAddressLookup(address)}>Look up</button>
                </div>
                {showSuggestions && (
                  <div className="suggestions">
                    {suggestions.map((s, i) => (
                      <div key={i} className="suggestion-item" onMouseDown={() => selectAddress(s)}>
                        <span><MapPin size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /></span>
                        <div>
                          <div className="suggestion-main">{s.main}</div>
                          <div className="suggestion-sub">{s.sub}</div>
                        </div>
                      </div>
                    ))}
                    <div className="powered-by"><Map size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Powered by Google Maps</div>
                  </div>
                )}
                {addressAutofilled && (
                  <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                    Auto-filled from your last lookup · <a href="#clear-saved-info" onClick={e => { e.preventDefault(); clearSavedInfo(); }} style={{ color: '#999', textDecoration: 'underline' }}>Clear</a>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {civicLoading && (
        <div className="loading"><Loader size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Looking up your voter information...</div>
      )}

      {hasResults && !civicLoading && (
        <LookupResults
          onReset={handleReset}
          civicData={civicData}
          civicError={civicError}
          setActiveTab={setActiveTab}
          county={county}
          mapsLoaded={mapsLoaded}
        />
      )}
    </div>
  );
};

export default VoterLookup;
